import classNames from 'classnames';
import _ from 'lodash';
import React, { Suspense } from 'react';
import { Link } from '../utils/router/v3Bridge';
import userPath from '../utils/url/userPath';
import styles from './Avatar.module.scss';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

// Avoid circular dependency
const UserDetailPreviewPopOver = React.lazy(
  () => import('../user/UserDetailPreviewPopOver'),
);

const THE_GOLDEN_RATIO = 1.61803398875;

const NUM_AVATAR_COLORS = 6;

gql(`
  fragment Avatar_avatar on Image {
    thumb
  }
`);

const USER_FRAGMENT = gql(`
  fragment Avatar_user on PublicUserFields {
    id
    avatar {
      ...Avatar_avatar
    }
    name
  }
`);

const INVITATION_FRAGMENT = gql(`
  fragment Avatar_invitation on Invitation {
    id
    avatar {
      ...Avatar_avatar
    }
    name
  }
`);

const EXTERNAL_USER_FRAGMENT = gql(`
  fragment Avatar_externalUser on ExternalUser {
    name
    avatar
  }
`);

type AvatarProps = {
  muted?: boolean;
  showHoverCard?: boolean;
  longHoverDelay?: boolean;
  size?: ('small' | 'regular' | 'medium' | 'large') | number;
} & (
  | {
      user: DocumentType<typeof USER_FRAGMENT>;
      link: true | { newTab: true };
    }
  | {
      user:
        | DocumentType<typeof USER_FRAGMENT>
        | DocumentType<typeof INVITATION_FRAGMENT>
        | (DocumentType<typeof EXTERNAL_USER_FRAGMENT> & { id?: never });
      link?: false;
    }
);

export const getInitials = (name = '') => {
  const letters = name
    .split(' ')
    .filter((token) => token)
    .map((token) => _.split(token, '')[0]?.toUpperCase());
  letters.splice(1, letters.length - 2);

  return letters.join('');
};

export const getAvatarColorClassName = (name: string) => {
  const code =
    _.split(name, '').reduce(
      (memo, char) => (memo + char.charCodeAt(0)) % NUM_AVATAR_COLORS,
      0,
    ) + 1;

  return styles[`color${code}`];
};

const Avatar = ({
  muted = false,
  showHoverCard = false,
  longHoverDelay = false,
  size = 'regular',
  ...props
}: AvatarProps) => {
  const { user } = props;
  if (user.name == null) return null;

  const thumb =
    typeof user.avatar === 'string' ? user.avatar : user.avatar?.thumb;

  const classes = classNames(styles.avatar, {
    [getAvatarColorClassName(user.name)]: !thumb,
    [styles.muted]: muted,
    [styles.large]: size === 'large',
    [styles.medium]: size === 'medium',
    [styles.small]: size === 'small',
  });

  const style =
    typeof size === 'number'
      ? {
          fontSize: Math.max(10, size / (1 + THE_GOLDEN_RATIO)),
          height: size,
          width: size,
        }
      : undefined;

  const avatar = (
    <div
      className={classes}
      style={style}
      title={showHoverCard ? undefined : user.name}
      role="img"
      aria-label={user.name}
    >
      {thumb ? <img src={thumb} alt={user.name} /> : getInitials(user.name)}
    </div>
  );

  const content = props.link ? (
    <Link
      to={userPath(props.user)}
      {...(typeof props.link === 'object' && props.link.newTab
        ? {
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : {})}
    >
      {avatar}
    </Link>
  ) : (
    avatar
  );

  if (showHoverCard && user.id) {
    return (
      <Suspense fallback={content}>
        <UserDetailPreviewPopOver
          userId={user.id}
          display="inline-block"
          placement="top-start"
          longHoverDelay={longHoverDelay}
        >
          {content}
        </UserDetailPreviewPopOver>
      </Suspense>
    );
  }
  return content;
};

export default Avatar;
